import React, { useState } from "react";
import { Link } from "react-router-dom";
import { appUrl, isCrossOrigin } from "@/lib/appUrl";
import {
  ArrowRight, ShieldCheck, Sparkles, Send, BarChart3, Workflow, Server,
  CheckCircle2, Mail, Eye, Zap, FileCheck, GitBranch, Users, Radio,
  ListChecks, Upload, Gauge, Filter, AlertTriangle, ChevronDown, Database,
  Clock, Code2, MousePointerClick, TrendingUp,
} from "lucide-react";
import { LANDING } from "@/constants/testIds";
import SendVertexLogo from "@/components/brand/SendVertexLogo";

// ─── Re-usable atoms ─────────────────────────────────────────────────────

const Stat = ({ k, v, sub }) => (
  <div className="border-l-2 border-slate-900 pl-4">
    <div className="font-display text-3xl font-black text-slate-950 leading-none">{v}</div>
    <div className="text-overline mt-1">{k}</div>
    {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
  </div>
);

const FeatureCard = ({ icon: Icon, title, desc, num, tag }) => (
  <div className="border border-slate-200 bg-white rounded-md p-6 hover:border-slate-900 transition-colors group">
    <div className="flex items-start justify-between mb-4">
      <div className="w-10 h-10 rounded-md bg-slate-900 text-white grid place-items-center group-hover:scale-110 transition-transform">
        <Icon className="w-5 h-5" />
      </div>
      <div className="font-mono text-xs text-slate-400">{num}</div>
    </div>
    {tag && <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-emerald-600 mb-1.5">{tag}</div>}
    <h3 className="font-display text-lg font-bold text-slate-950 tracking-tight mb-2">{title}</h3>
    <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
  </div>
);

const Pill = ({ children, tone = "slate" }) => {
  const tones = {
    slate: "border-slate-300 bg-white text-slate-700",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    sky: "border-sky-200 bg-sky-50 text-sky-700",
  };
  return <span className={`inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider px-2 py-1 rounded-full border ${tones[tone]}`}>{children}</span>;
};

// ─── Page ────────────────────────────────────────────────────────────────

// Cross-domain aware link — routes /login and /register to app.sendvertex.com
// when REACT_APP_APP_URL is set. Otherwise behaves like a normal <Link>.
function AuthLink({ to, children, ...props }) {
  if (isCrossOrigin(to)) {
    return (
      <a href={appUrl(to)} {...props}>{children}</a>
    );
  }
  return (
    <Link to={to} {...props}>{children}</Link>
  );
}


export default function Landing() {
  return (
    <div className="min-h-screen bg-slate-50" data-testid={LANDING.root}>
      <Header />
      <Hero />
      <TrustStrip />
      <Pillars />
      <VerificationDeepDive />
      <BuilderDeepDive />
      <ImportSplitDeepDive />
      <DeliverabilitySuite />
      <AnalyticsPreview />
      <FullFeatureMatrix />
      <ComparisonTable />
      <FAQ />
      <FinalCTA />
      <Footer />
    </div>
  );
}

// ─── Header ──────────────────────────────────────────────────────────────

function Header() {
  return (
    <header className="border-b border-slate-200 bg-white sticky top-0 z-50 backdrop-blur-xl bg-white/85">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center">
          <SendVertexLogo
            size={44}
            wordmarkClassName="text-lg sm:text-xl leading-none tracking-tight"
            className="items-center gap-2.5"
          />
        </div>
        <nav className="hidden md:flex items-center gap-7 text-sm text-slate-600">
          <a href="#features" className="hover:text-slate-900">Features</a>
          <a href="#verification" className="hover:text-slate-900">Verification</a>
          <a href="#builder" className="hover:text-slate-900">Builder</a>
          <a href="#deliverability" className="hover:text-slate-900">Deliverability</a>
          <a href="#faq" className="hover:text-slate-900">FAQ</a>
        </nav>
        <div className="flex items-center gap-2">
          <AuthLink to="/login" data-testid={LANDING.ctaLogin} className="text-sm px-3 py-1.5 rounded-md hover:bg-slate-100 text-slate-700">Log in</AuthLink>
          <AuthLink to="/register" data-testid={LANDING.ctaSignup} className="text-sm px-4 py-2 rounded-md bg-slate-900 text-white hover:bg-slate-800">
            Get started <ArrowRight className="w-3.5 h-3.5 inline ml-1" />
          </AuthLink>
        </div>
      </div>
    </header>
  );
}

// ─── Hero ────────────────────────────────────────────────────────────────

function Hero() {
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-50" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-6 pb-24">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 border border-slate-300 bg-white rounded-full px-3 py-1 text-xs font-mono">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Built for senders who measure inbox placement, not just opens
            </div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter text-slate-950 leading-[1.05] mt-6">
              The email engine<br />
              <span className="text-slate-400">that ships clean lists</span><br />
              and pays its bounces forward.
            </h1>
            <p className="text-base sm:text-lg text-slate-600 mt-6 max-w-xl leading-relaxed">
              Drag-and-drop builder. 20-check real-time verification. Custom-split imports.
              CAN-SPAM lint, throttle enforcement, auto-suppression. Real-time analytics.
              <span className="font-mono text-slate-900"> One platform.</span>
            </p>
            <div className="flex items-center gap-3 mt-8">
              <AuthLink to="/register" className="inline-flex items-center gap-2 px-5 py-3 bg-slate-900 text-white rounded-md text-sm font-medium hover:bg-slate-800">
                Start free — no card required <ArrowRight className="w-4 h-4" />
              </AuthLink>
              <a href="#features" className="inline-flex items-center gap-2 px-5 py-3 border border-slate-300 bg-white text-slate-900 rounded-md text-sm font-medium hover:bg-slate-100">
                Tour the platform
              </a>
            </div>
            <div className="grid grid-cols-3 gap-6 mt-12 max-w-xl">
              <Stat k="Inbox rate" v="99.4%" sub="Across top ISPs" />
              <Stat k="Verify accuracy" v="99%" sub="20 native checks" />
              <Stat k="Setup time" v="< 5 min" sub="From signup to first send" />
            </div>
          </div>
          <div className="lg:col-span-5">
            <HeroCampaignCard />
          </div>
        </div>
      </div>
    </section>
  );
}

function HeroCampaignCard() {
  return (
    <div className="bg-white border border-slate-200 rounded-md shadow-xl overflow-hidden">
      <div className="px-4 py-2 border-b border-slate-200 flex items-center gap-2 bg-slate-50">
        <div className="w-2.5 h-2.5 rounded-full bg-slate-300" />
        <div className="w-2.5 h-2.5 rounded-full bg-slate-300" />
        <div className="w-2.5 h-2.5 rounded-full bg-slate-300" />
        <span className="font-mono text-[11px] text-slate-500 ml-2">sendvertex / campaign / blackfriday</span>
      </div>
      <div className="p-5 space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div className="border border-slate-200 rounded-md p-3"><div className="text-overline">SENT</div><div className="font-display text-xl font-black mt-1">128,402</div></div>
          <div className="border border-slate-200 rounded-md p-3"><div className="text-overline">OPEN</div><div className="font-display text-xl font-black mt-1 text-emerald-600">42.7%</div></div>
          <div className="border border-slate-200 rounded-md p-3"><div className="text-overline">CLICK</div><div className="font-display text-xl font-black mt-1 text-blue-600">8.3%</div></div>
        </div>
        <div className="h-32 border border-slate-200 rounded-md p-3 bg-gradient-to-br from-slate-50 to-white relative overflow-hidden">
          <div className="text-overline">DELIVERY TIMELINE</div>
          <svg viewBox="0 0 200 60" className="w-full h-20 mt-2">
            <polyline points="0,40 20,30 40,32 60,22 80,18 100,12 120,15 140,8 160,10 180,5 200,7" fill="none" stroke="#0f172a" strokeWidth="1.5" />
            <polyline points="0,40 20,30 40,32 60,22 80,18 100,12 120,15 140,8 160,10 180,5 200,7 200,60 0,60" fill="rgba(15,23,42,0.06)" />
          </svg>
        </div>
        <div className="space-y-2">
          {[
            { label: "SMTP probe · clean IP pool", status: "live", color: "bg-emerald-500" },
            { label: "DKIM · sv._domainkey", status: "verified", color: "bg-emerald-500" },
            { label: "CAN-SPAM compliance check", status: "passed", color: "bg-emerald-500" },
            { label: "Throttle · 600/hr · 20/min", status: "enforced", color: "bg-emerald-500" },
          ].map((r) => (
            <div key={r.label} className="flex items-center justify-between text-xs border border-slate-200 rounded-md px-3 py-2">
              <span className="font-mono">{r.label}</span>
              <span className="flex items-center gap-1.5"><span className={`w-1.5 h-1.5 rounded-full ${r.color}`} />{r.status}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── Trust strip ─────────────────────────────────────────────────────────

function TrustStrip() {
  const items = [
    { v: "20", k: "Verification checks per email" },
    { v: "412", k: "Pre-send compliance errors caught" },
    { v: "8", k: "Suppression reason codes tracked" },
    { v: "100%", k: "CAN-SPAM compliant by design" },
  ];
  return (
    <section className="border-t border-b border-slate-200 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
        {items.map((it) => (
          <div key={it.k} className="text-center">
            <div className="font-display text-4xl sm:text-5xl font-black text-slate-900 tabular-nums">{it.v}</div>
            <div className="text-overline mt-2">{it.k}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ─── Three Pillars ───────────────────────────────────────────────────────

function Pillars() {
  const [active, setActive] = useState(0);
  const pillars = [
    {
      eyebrow: "01 · BUILD",
      title: "Compose with code-mode or blocks",
      icon: Send,
      body: "Drag-and-drop block editor, a full HTML/CSS code mode for power users, mobile-first responsive output, live preview across desktop and mobile, A/B testing on subject + content, AI subject generator with predicted open rates, and a spam score predictor before you send.",
      bullets: [
        "4-step wizard: Details → Content → Audience → Schedule",
        "Drag-and-drop blocks: text, heading, image, button, list, divider, footer",
        "Full HTML/CSS code mode with live preview",
        "A/B test variants on subject + content",
        "Save reusable templates · personalization tokens",
      ],
    },
    {
      eyebrow: "02 · VERIFY",
      title: "20-check engine — clean lists, every time",
      icon: ShieldCheck,
      body: "Built-in real-time verification with 20 simultaneous checks per email: syntax, domain validity, MX records, SPF/DKIM/DMARC, full SMTP RCPT handshake, catch-all, greylist, disposable, role-based, spam-traps, toxic patterns — all running natively. Drag-and-drop bulk CSV verification with auto-bucketing into Good / Quarantine / Suppression lists.",
      bullets: [
        "20 simultaneous checks per email · DNS, SMTP, heuristic",
        "Drag-and-drop bulk CSV verify · auto-buckets results",
        "Per-job category download — slice by 12 result categories",
        "List Health monitor on every campaign",
        "Verification history forever — re-download any past job",
      ],
    },
    {
      eyebrow: "03 · DELIVER",
      title: "Send with the deliverability suite built in",
      icon: Zap,
      body: "Pre-send CAN-SPAM lint blocks campaigns missing unsubscribe, postal address, or sender identifier. Send-time throttle enforcement honors your per-minute and per-hour caps. Auto-suppression on hard bounces and 5+ soft bounces. Bounce classification with reason codes. List-Unsubscribe headers for Gmail's one-click unsubscribe.",
      bullets: [
        "CAN-SPAM lint — 5 hard requirements + 4 advisory warnings",
        "Throttle: enforce per-hour AND per-minute caps simultaneously",
        "Auto-suppress hard bounces + repeated soft bounces (5+)",
        "Bounce classifier · 8 reason codes · severity routing",
        "List-Unsubscribe + List-Unsubscribe-Post (Gmail one-click)",
      ],
    },
  ];

  return (
    <section className="border-b border-slate-200 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="text-overline mb-3">How SendVertex works</div>
        <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-slate-950 max-w-3xl">
          Three pillars · one platform · zero glue code.
        </h2>
        <div className="grid lg:grid-cols-3 gap-3 mt-10">
          {pillars.map((p, i) => {
            const isActive = active === i;
            const Icon = p.icon;
            return (
              <button
                key={p.eyebrow}
                onClick={() => setActive(i)}
                className={`text-left p-6 border rounded-md transition-all ${
                  isActive ? "border-slate-900 bg-white shadow-lg" : "border-slate-200 bg-white hover:border-slate-400"
                }`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className={`w-10 h-10 rounded-md grid place-items-center ${isActive ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700"}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-400">{p.eyebrow}</div>
                </div>
                <h3 className="font-display text-xl font-bold text-slate-950 mb-2">{p.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{p.body}</p>
              </button>
            );
          })}
        </div>
        <div className="border border-slate-900 bg-slate-900 text-white rounded-md p-8 mt-3" key={active}>
          <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-emerald-400 mb-3">// {pillars[active].eyebrow.toLowerCase().replace(/[^a-z]/g, "_")}.detail</div>
          <ul className="grid sm:grid-cols-2 gap-3 mt-2">
            {pillars[active].bullets.map((b) => (
              <li key={b} className="flex items-start gap-2.5 text-sm">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                <span className="text-slate-200">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

// ─── Verification Deep Dive ──────────────────────────────────────────────

function VerificationDeepDive() {
  const checks = [
    "Syntax (RFC-5322)", "Typo correction", "Disposable detection", "Role-based filter",
    "Free-provider tag", "MX records", "SPF lookup", "DKIM lookup",
    "DMARC policy", "SMTP connect", "SMTP RCPT TO", "Catch-all probe",
    "Greylist detect", "Spam-trap match", "Toxic patterns", "Hard-bounce history",
    "Soft-bounce history", "Domain age", "Mailbox full", "Policy-block detect",
  ];
  const buckets = [
    { label: "Good", tone: "emerald", count: "8,914", desc: "Send-ready — kept on your active list" },
    { label: "Quarantine", tone: "amber", count: "1,283", desc: "Risky but recoverable — review before send" },
    { label: "Suppression", tone: "red", count: "403", desc: "Never send — disposable, traps, hard rejects" },
  ];
  return (
    <section id="verification" className="border-b border-slate-200 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <Pill tone="emerald"><ShieldCheck className="w-3 h-3" /> Verification Engine</Pill>
            <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-slate-950 mt-4 leading-none">
              20 checks.<br />One score.<br />Zero bounces.
            </h2>
            <p className="text-slate-600 mt-6 leading-relaxed max-w-md">
              Every email runs through twenty parallel checks before it ever leaves your list.
              Syntax, MX, SPF, DKIM, DMARC, real SMTP handshakes — no third-party API bills,
              no rate limits, no waiting.
            </p>
            <div className="grid grid-cols-3 gap-2 mt-6">
              {buckets.map((b) => (
                <div key={b.label} className={`border rounded-md p-3 border-${b.tone}-200 bg-${b.tone}-50`}>
                  <div className={`text-overline text-${b.tone}-700`}>{b.label}</div>
                  <div className={`font-display text-2xl font-black mt-0.5 text-${b.tone}-700`}>{b.count}</div>
                  <div className="text-[11px] text-slate-600 mt-1 leading-snug">{b.desc}</div>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-6">
              <Pill tone="sky"><Upload className="w-3 h-3" /> Drag-drop CSV</Pill>
              <Pill tone="sky"><Filter className="w-3 h-3" /> 12 download categories</Pill>
              <Pill tone="sky"><Clock className="w-3 h-3" /> History forever</Pill>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="border border-slate-200 rounded-md bg-slate-50 p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="text-overline">Live verification pipeline</div>
                <Pill tone="emerald"><Radio className="w-3 h-3" /> All 20 checks pass</Pill>
              </div>
              <div className="grid grid-cols-4 gap-1.5">
                {checks.map((c, i) => (
                  <div key={c} className="bg-white border border-slate-200 rounded px-2 py-1.5 flex items-center gap-1.5 text-[11px] font-mono">
                    <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" />
                    <span className="truncate text-slate-700" title={c}>{String(i + 1).padStart(2, "0")} {c}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 border border-slate-200 rounded-md bg-white p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-overline">Sample · sarah@stripe.com</div>
                  <div className="font-display text-2xl font-black text-emerald-600">96</div>
                </div>
                <div className="grid grid-cols-2 gap-1 text-[11px] font-mono text-slate-600">
                  <div>mx_host: <span className="text-slate-900">mx-cust1.mailhop.org</span></div>
                  <div>smtp_accepted: <span className="text-emerald-600">true</span></div>
                  <div>catch_all: <span className="text-slate-900">false</span></div>
                  <div>disposable: <span className="text-slate-900">false</span></div>
                  <div>spf: <span className="text-emerald-600">pass</span></div>
                  <div>dkim: <span className="text-emerald-600">pass</span></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Builder Deep Dive ───────────────────────────────────────────────────

function BuilderDeepDive() {
  const tabs = [
    { num: "1", label: "Details", body: "Name, subject, preheader, from name + from email, reply-to." },
    { num: "2", label: "Content", body: "Drag-and-drop blocks · code mode · live preview · A/B variants." },
    { num: "3", label: "Audience", body: "Pick lists, layer dynamic segments, exclude suppressed." },
    { num: "4", label: "Schedule", body: "Send now · at a specific time · split schedule per list. Throttle caps." },
  ];
  return (
    <section id="builder" className="border-b border-slate-200 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <div className="bg-white border border-slate-200 rounded-md overflow-hidden shadow-lg">
              <div className="flex border-b border-slate-200">
                {tabs.map((t, i) => (
                  <div key={t.label} className={`flex-1 px-3 py-3 border-r border-slate-200 last:border-0 ${i === 1 ? "bg-slate-50" : ""}`}>
                    <div className="flex items-center gap-2">
                      <div className={`w-5 h-5 rounded-full grid place-items-center text-[10px] font-bold ${i === 1 ? "bg-slate-900 text-white" : "bg-slate-200 text-slate-500"}`}>{t.num}</div>
                      <div className={`text-sm font-medium ${i === 1 ? "text-slate-900" : "text-slate-500"}`}>{t.label}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-12 gap-0">
                <div className="col-span-3 border-r border-slate-200 p-3 bg-slate-50">
                  <div className="text-overline mb-2">Blocks</div>
                  <div className="space-y-1.5">
                    {["Heading", "Text", "Image", "Button", "Divider", "List", "Footer"].map((b) => (
                      <div key={b} className="text-xs border border-slate-200 bg-white rounded px-2 py-1.5 font-mono cursor-grab hover:border-slate-900">{b}</div>
                    ))}
                  </div>
                </div>
                <div className="col-span-9 p-5 min-h-[280px]">
                  <div className="text-overline mb-3">Visual preview</div>
                  <div className="bg-slate-50 rounded-md p-4 space-y-3 max-w-md mx-auto">
                    <div className="h-6 bg-slate-200 rounded w-3/4" />
                    <div className="h-3 bg-slate-200 rounded" />
                    <div className="h-3 bg-slate-200 rounded w-5/6" />
                    <div className="h-3 bg-slate-200 rounded w-2/3" />
                    <div className="h-8 bg-slate-900 rounded w-32 mt-3" />
                    <div className="h-2 bg-slate-200 rounded w-1/2 mt-4" />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-5 order-1 lg:order-2">
            <Pill><Send className="w-3 h-3" /> Campaign Builder</Pill>
            <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-slate-950 mt-4 leading-none">
              Build the email.<br />Skip the markup.
            </h2>
            <p className="text-slate-600 mt-6 leading-relaxed max-w-md">
              A 4-step wizard that knows what comes next. Compose with drag-and-drop blocks
              or drop into full code mode. Add A/B variants on subject + content. Personalize
              with merge tags. Preview every device before you commit.
            </p>
            <div className="space-y-3 mt-6">
              {tabs.map((t) => (
                <div key={t.label} className="flex items-start gap-3 border border-slate-200 bg-white rounded-md p-3">
                  <div className="w-7 h-7 rounded-md bg-slate-100 grid place-items-center text-xs font-bold text-slate-700 shrink-0">{t.num}</div>
                  <div>
                    <div className="font-semibold text-sm text-slate-900">{t.label}</div>
                    <div className="text-xs text-slate-600 mt-0.5">{t.body}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Import Wizard Deep Dive ─────────────────────────────────────────────

function ImportSplitDeepDive() {
  return (
    <section className="border-b border-slate-200 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <Pill tone="sky"><GitBranch className="w-3 h-3" /> Import + Split</Pill>
            <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-slate-950 mt-4 leading-none">
              Custom-named splits.<br />Imported clean.<br />Previewed first.
            </h2>
            <p className="text-slate-600 mt-6 leading-relaxed max-w-md">
              Upload a CSV. Name a parent list. Carve it into named sub-lists by count,
              percentage, or column value — then preview exactly which rows land where
              before you commit. No round-robin guesswork.
            </p>
            <div className="mt-6 space-y-2">
              {[
                { icon: ListChecks, label: "by count", body: "Split1 = 100 contacts · Split2 = 500 contacts" },
                { icon: BarChart3, label: "by percentage", body: "Split1 = 30% · Split2 = 50% — random distribution" },
                { icon: Filter, label: "by column", body: "country='US' · vertical='SaaS' · optional max count" },
              ].map((m) => {
                const Icon = m.icon;
                return (
                  <div key={m.label} className="border border-slate-200 rounded-md p-3 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-md bg-slate-100 grid place-items-center text-slate-700"><Icon className="w-4 h-4" /></div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider font-mono text-slate-500">{m.label}</div>
                      <div className="text-xs text-slate-700 font-mono mt-0.5">{m.body}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="border border-slate-200 rounded-md bg-slate-50 p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="text-overline">SPLIT PREVIEW · DRY RUN</div>
                <Pill tone="emerald"><Eye className="w-3 h-3" /> 20 rows · 2 splits</Pill>
              </div>
              <div className="space-y-2">
                {[
                  { tone: "emerald", name: "US_Users", count: 8, sample: ["sarah@stripe.com", "jane@figma.com", "alex@linear.app", "max@vercel.com", "kim@notion.so"] },
                  { tone: "emerald", name: "IN_Users", count: 7, sample: ["raj@razorpay.com", "priya@swiggy.com", "arjun@phonepe.com", "neha@cred.club", "vikram@zomato.com"] },
                  { tone: "slate", name: "Stays on parent only", count: 5, sample: ["liam@gov.uk", "emma@bbc.co.uk", "lena@sap.com", "kurt@bmw.de"] },
                ].map((b) => (
                  <div key={b.name} className={`border rounded-md overflow-hidden border-${b.tone}-200`}>
                    <div className={`px-3 py-2 bg-${b.tone}-50 flex items-center justify-between`}>
                      <div className="font-display font-bold text-sm">{b.name}</div>
                      <div className={`text-xs font-mono font-bold text-${b.tone}-700`}>{b.count} contacts</div>
                    </div>
                    <div className="px-3 py-2 bg-white">
                      <div className="text-[11px] font-mono text-slate-600 space-y-0.5">
                        {b.sample.slice(0, 3).map((e) => <div key={e}>{e}</div>)}
                        {b.count > 3 && <div className="text-slate-400">+ {b.count - 3} more</div>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Deliverability Suite ────────────────────────────────────────────────

function DeliverabilitySuite() {
  const checks = [
    { ok: true,  label: "Subject line", desc: "Honest, < 80 chars" },
    { ok: true,  label: "From name", desc: "Recognizable sender" },
    { ok: true,  label: "From email", desc: "Resolves to your domain" },
    { ok: false, label: "Unsubscribe link", desc: "Required by CAN-SPAM §5(a)(3)" },
    { ok: true,  label: "Physical address", desc: "Required by CAN-SPAM §5(a)(5)" },
  ];
  return (
    <section id="deliverability" className="bg-slate-950 text-white border-b border-slate-800">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-emerald-400 mb-3">// deliverability.suite</div>
            <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter leading-none">
              Compliance, throttle,<br />and bounce handling —<br />
              <span className="text-slate-400">automatic.</span>
            </h2>
            <p className="text-slate-300 mt-6 leading-relaxed max-w-lg">
              Every campaign passes a CAN-SPAM lint before it sends. Throttle caps are
              enforced at the dispatcher, not just stored. Hard bounces auto-suppress.
              Five+ soft bounces auto-suppress. Suppressions cross-check mid-flight.
            </p>
            <ul className="mt-8 space-y-3 text-sm">
              {[
                { icon: FileCheck, label: "CAN-SPAM pre-send lint — 5 hard requirements + 4 advisory warnings" },
                { icon: Gauge, label: "Throttle: per-hour AND per-minute caps · honest enforcement" },
                { icon: AlertTriangle, label: "Auto-suppress on hard bounce + 5 consecutive soft bounces" },
                { icon: Database, label: "Suppression cross-ref on every send (catches mid-flight blocks)" },
                { icon: Radio, label: "List-Unsubscribe-Post — Gmail one-click compatible" },
                { icon: Sparkles, label: "Bounce classifier · 8 reason codes · severity routing" },
              ].map((s) => {
                const Icon = s.icon;
                return (
                  <li key={s.label} className="flex items-start gap-3">
                    <Icon className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
                    <span>{s.label}</span>
                  </li>
                );
              })}
            </ul>
          </div>
          <div className="lg:col-span-7">
            <div className="bg-black/30 backdrop-blur border border-slate-800 rounded-md p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-amber-400">CAN-SPAM check</div>
                <div className="text-xs font-mono font-bold text-amber-400">1 error · 0 warnings</div>
              </div>
              <div className="grid grid-cols-5 gap-px bg-slate-800 rounded overflow-hidden">
                {checks.map((c) => (
                  <div key={c.label} className="bg-slate-900 p-3 text-center">
                    <div className={`text-2xl ${c.ok ? "text-emerald-400" : "text-red-400"}`}>{c.ok ? "✓" : "✗"}</div>
                    <div className="text-[10px] uppercase tracking-wider font-mono text-slate-400 mt-1">{c.label}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 border-l-2 border-red-500 pl-3 text-sm">
                <div className="font-semibold text-white">No unsubscribe link found.</div>
                <div className="text-slate-400 text-xs mt-1">CAN-SPAM requires a working opt-out mechanism in every commercial email.</div>
                <div className="font-mono text-[11px] mt-2 text-emerald-400">→ Fix: Add a Footer block with the {`{{unsubscribe_url}}`} placeholder.</div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-5">
                <div className="border border-slate-800 rounded-md p-3 bg-slate-900/50">
                  <div className="text-[10px] uppercase tracking-wider font-mono text-slate-400">Throttle enforcement</div>
                  <div className="font-display text-xl font-black text-emerald-400 mt-1">600 / hr</div>
                  <div className="text-xs text-slate-500 mt-0.5">also: 20 / min · dominant cap wins</div>
                </div>
                <div className="border border-slate-800 rounded-md p-3 bg-slate-900/50">
                  <div className="text-[10px] uppercase tracking-wider font-mono text-slate-400">Live suppressions</div>
                  <div className="font-display text-xl font-black text-amber-400 mt-1">1,847</div>
                  <div className="text-xs text-slate-500 mt-0.5">hard · soft · complaint · manual</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ─── Analytics Preview ───────────────────────────────────────────────────

function AnalyticsPreview() {
  return (
    <section className="border-b border-slate-200 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="text-center max-w-3xl mx-auto">
          <Pill tone="sky"><TrendingUp className="w-3 h-3" /> Analytics</Pill>
          <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-slate-950 mt-4">
            Every metric. One screen. Real time.
          </h2>
          <p className="text-slate-600 mt-4">
            Opens, clicks, bounces by reason code, list health correlation, verification scores,
            geo and device breakdowns, A/B variant winners — refreshed every second.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3 mt-10">
          {[
            { icon: MousePointerClick, label: "Open & click rates", desc: "Per campaign, per variant, per segment" },
            { icon: AlertTriangle, label: "Bounce taxonomy", desc: "8 reason codes · severity-routed suppressions" },
            { icon: ShieldCheck, label: "List Health card", desc: "Verification score vs bounce-rate correlation" },
            { icon: TrendingUp, label: "Send-rate timeline", desc: "Throttle pacing visible by the second" },
            { icon: Users, label: "Segment breakdowns", desc: "Custom-field aware · refresh-on-demand" },
            { icon: Filter, label: "Verification history", desc: "Every job · downloadable by category forever" },
            { icon: BarChart3, label: "A/B variant winner", desc: "Statistical significance + 'apply to remainder'" },
            { icon: Database, label: "Suppression analytics", desc: "Source · reason · trend lines" },
          ].map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.label} className="border border-slate-200 rounded-md p-4 hover:border-slate-900 transition-colors">
                <div className="w-8 h-8 rounded-md bg-slate-100 grid place-items-center mb-3"><Icon className="w-4 h-4 text-slate-700" /></div>
                <div className="font-semibold text-sm text-slate-900">{s.label}</div>
                <div className="text-xs text-slate-500 mt-1 leading-snug">{s.desc}</div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

// ─── Full Feature Matrix ─────────────────────────────────────────────────

function FullFeatureMatrix() {
  return (
    <section id="features" className="border-b border-slate-200 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="flex items-end justify-between gap-6 flex-wrap mb-10">
          <div>
            <div className="text-overline mb-3">Capabilities</div>
            <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-slate-950 max-w-2xl">
              Everything in SendVertex — at a glance.
            </h2>
          </div>
          <div className="text-sm text-slate-500 font-mono">v1.0 · ships weekly</div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          <FeatureCard num="01" tag="BUILD" icon={Send} title="Drag-and-drop Builder" desc="Block-based composer with 7 native blocks, mobile-first responsive output, live preview, and a code-mode escape hatch for HTML/CSS power users." />
          <FeatureCard num="02" tag="BUILD" icon={Code2} title="Visual + Code Modes" desc="Switch between drag-drop and full HTML/CSS in one click. Code edits round-trip back to visual blocks where possible." />
          <FeatureCard num="03" tag="BUILD" icon={Sparkles} title="AI Studio" desc="Subject-line generation with predicted open rates · spam-score predictor before send · tone analysis · content rewrites." />
          <FeatureCard num="04" tag="VERIFY" icon={ShieldCheck} title="20-Check Verification" desc="Real SMTP RCPT TO probes · MX, SPF, DKIM, DMARC · disposable, role, spam-trap, catch-all, greylist detection." />
          <FeatureCard num="05" tag="VERIFY" icon={Upload} title="Drag-Drop Bulk Verify" desc="Drop a CSV. Auto-buckets results into Good / Quarantine / Suppression lists. Re-download any category later." />
          <FeatureCard num="06" tag="VERIFY" icon={Clock} title="Verification History" desc="Every job — upload or list — stored forever. Click any past job's category badge to re-download as CSV." />
          <FeatureCard num="07" tag="LISTS" icon={ListChecks} title="Lists & Segments" desc="Static lists + dynamic segments with multi-condition logic. Refresh-on-demand. Custom-field aware." />
          <FeatureCard num="08" tag="LISTS" icon={GitBranch} title="Custom-Split Imports" desc="Carve a CSV into named sub-lists by count, percentage, or column value. Preview before commit. Parent list keeps everyone." />
          <FeatureCard num="09" tag="LISTS" icon={Database} title="Custom Fields" desc="Any CSV column maps to a queryable custom field. Use in personalization tokens and segment conditions." />
          <FeatureCard num="10" tag="DELIVER" icon={FileCheck} title="CAN-SPAM Lint" desc="Pre-send check — blocks campaigns missing subject, from, unsubscribe, or postal address. 4 advisory warnings on top." />
          <FeatureCard num="11" tag="DELIVER" icon={Gauge} title="Throttle Enforcement" desc="Per-hour AND per-minute caps honored simultaneously. Dominant cap wins. Honest dispatcher pacing — no surprises." />
          <FeatureCard num="12" tag="DELIVER" icon={AlertTriangle} title="Auto Suppression" desc="Hard bounces suppress immediately. 5+ soft bounces auto-suppress. Mid-flight cross-ref catches concurrent blocks." />
          <FeatureCard num="13" tag="DELIVER" icon={Radio} title="One-Click Unsubscribe" desc="List-Unsubscribe + List-Unsubscribe-Post headers on every send. Gmail compatible. Bounce → suppression → contact-status pipeline." />
          <FeatureCard num="14" tag="ANALYTICS" icon={BarChart3} title="Real-time Analytics" desc="Opens, clicks, bounces with reason codes, geo, device, A/B winners. Refreshed every second via tracking pixel + click redirect." />
          <FeatureCard num="15" tag="ANALYTICS" icon={TrendingUp} title="List Health Card" desc="Every campaign shows verification-score vs bounce-rate correlation. Spot list rot before it costs you inbox placement." />
          <FeatureCard num="16" tag="SEND" icon={Workflow} title="Schedule + Split Send" desc="Send immediately, schedule at a specific time, or run a split schedule with a different send time per list." />
          <FeatureCard num="17" tag="SEND" icon={Server} title="BYO SMTP" desc="Connect any SMTP server — your own MTA, SES, SendGrid, Mailgun, Postal. Multiple profiles per workspace, per-campaign selection." />
          <FeatureCard num="18" tag="OPS" icon={Users} title="Multi-tenant Workspaces" desc="Owner-managed brand metadata (company name, postal address, support email) feeds the compliance linter and footer injection." />
        </div>
      </div>
    </section>
  );
}

// ─── Comparison ──────────────────────────────────────────────────────────

function ComparisonTable() {
  const rows = [
    { f: "Real-time verification (20 checks, no external API bill)", mc: true, mc2: true, gen: false },
    { f: "Custom-named import splits with dry-run preview", mc: true, mc2: false, gen: false },
    { f: "CAN-SPAM lint blocks sends pre-flight", mc: true, mc2: false, gen: false },
    { f: "Throttle enforcement at the dispatcher (not just stored)", mc: true, mc2: false, gen: false },
    { f: "List-Unsubscribe + One-Click Gmail header", mc: true, mc2: true, gen: true },
    { f: "Auto-suppress on hard + repeated soft bounces", mc: true, mc2: true, gen: true },
    { f: "BYO SMTP · no provider lock-in", mc: true, mc2: false, gen: false },
    { f: "Drag-drop builder + HTML code mode in same campaign", mc: true, mc2: true, gen: true },
    { f: "Verification history forever, re-downloadable by category", mc: true, mc2: false, gen: false },
  ];
  return (
    <section className="border-b border-slate-200 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="text-overline mb-3">How we compare</div>
        <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-slate-950 max-w-2xl">
          What you get with SendVertex, and what you don&apos;t get elsewhere.
        </h2>
        <div className="mt-10 border border-slate-200 rounded-md overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="text-left px-5 py-3 text-overline">Feature</th>
                <th className="text-center px-3 py-3 text-overline">SendVertex</th>
                <th className="text-center px-3 py-3 text-overline">Mainstream ESPs</th>
                <th className="text-center px-3 py-3 text-overline">Generic SMTP relay</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.f} className="border-b border-slate-100 last:border-0">
                  <td className="px-5 py-3 text-slate-700">{r.f}</td>
                  <td className="text-center px-3 py-3"><Mark v={r.mc} /></td>
                  <td className="text-center px-3 py-3"><Mark v={r.mc2} /></td>
                  <td className="text-center px-3 py-3"><Mark v={r.gen} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}

function Mark({ v }) {
  return v
    ? <span className="inline-flex w-5 h-5 rounded-full bg-emerald-500 text-white grid place-items-center"><CheckCircle2 className="w-3 h-3" /></span>
    : <span className="text-slate-300 font-mono text-base">—</span>;
}

// ─── FAQ ─────────────────────────────────────────────────────────────────

function FAQ() {
  const qs = [
    {
      q: "Is SendVertex self-hosted or hosted?",
      a: "SendVertex runs as a hosted SaaS today. You bring your own SMTP server (any provider — your own MTA, SES, SendGrid, Mailgun, Postal) and we handle everything else: builder, verification, deliverability checks, suppressions, analytics, and dispatching with throttle enforcement.",
    },
    {
      q: "Will SendVertex replace my verification tool (ZeroBounce, NeverBounce, Kickbox)?",
      a: "Yes — for ~99% of senders. Our engine runs 20 native checks per email including a real SMTP RCPT TO handshake. No external API calls, no per-verification bills, no rate limits. You can verify a 100k list with one drag-and-drop.",
    },
    {
      q: "Can I run cold outreach with SendVertex?",
      a: "Yes — and the deliverability suite is designed for it. Throttle per-hour AND per-minute caps are honored at the dispatcher. CAN-SPAM lint blocks sends missing unsubscribe/postal-address. Hard bounces auto-suppress. List-Unsubscribe-Post headers ship on every email.",
    },
    {
      q: "Does SendVertex store my contacts forever?",
      a: "Your contacts live in your workspace until you delete them. Suppressions persist independently so a deleted-then-re-added contact still respects past hard bounces. GDPR deletion endpoints are on the roadmap (Phase 6).",
    },
    {
      q: "What's the pricing?",
      a: "Free tier coming soon with generous limits. Paid plans (Starter, Growth, Scale) will be available shortly. Until then, every signup gets full access to all features.",
    },
    {
      q: "Can I import contacts in bulk?",
      a: "Yes — drag-and-drop CSV import with field mapping, deduplication (skip/update/overwrite), custom-fields capture, and named-split logic that lets you carve a 50k list into target sub-lists by count, percentage, or column value — all previewed before you commit.",
    },
    {
      q: "What happens to bounced emails?",
      a: "Every send is classified into one of 8 bounce reason codes. Hard bounces are added to your suppression list automatically. 5+ consecutive soft bounces also auto-suppress. Suppressions are cross-referenced mid-send so concurrent campaigns honor each other's results.",
    },
  ];
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="border-b border-slate-200 bg-slate-50">
      <div className="max-w-3xl mx-auto px-6 lg:px-8 py-20">
        <div className="text-overline mb-3 text-center">FAQ</div>
        <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter text-slate-950 text-center">
          Questions, answered honestly.
        </h2>
        <div className="mt-10 space-y-2">
          {qs.map((q, i) => (
            <div key={q.q} className="border border-slate-200 bg-white rounded-md overflow-hidden">
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full px-5 py-4 flex items-center justify-between text-left hover:bg-slate-50"
                data-testid={`faq-q-${i}`}
              >
                <span className="font-medium text-slate-900">{q.q}</span>
                <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${open === i ? "rotate-180" : ""}`} />
              </button>
              {open === i && (
                <div className="px-5 pb-4 text-sm text-slate-600 leading-relaxed border-t border-slate-100 pt-3">{q.a}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Final CTA ───────────────────────────────────────────────────────────

function FinalCTA() {
  return (
    <section id="pricing" className="border-b border-slate-200 bg-white">
      <div className="max-w-4xl mx-auto px-6 lg:px-8 py-20 text-center">
        <h2 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-slate-950">
          Ready to ship cleaner, faster email?
        </h2>
        <p className="text-slate-600 mt-4 max-w-xl mx-auto">
          Start free. Connect your SMTP. Send your first verified, compliant campaign in under five minutes.
        </p>
        <div className="flex items-center justify-center gap-3 mt-8 flex-wrap">
          <AuthLink to="/register" className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-md text-sm font-medium hover:bg-slate-800">
            Create your workspace <ArrowRight className="w-4 h-4" />
          </AuthLink>
          <AuthLink to="/login" className="inline-flex items-center gap-2 px-6 py-3 border border-slate-300 bg-white text-slate-900 rounded-md text-sm font-medium hover:bg-slate-100">
            Log in
          </AuthLink>
        </div>
        <div className="flex items-center justify-center gap-4 mt-8 text-xs text-slate-500 font-mono flex-wrap">
          <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-emerald-500" /> No credit card</span>
          <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-emerald-500" /> 5-minute setup</span>
          <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-emerald-500" /> Bring your own SMTP</span>
          <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3 h-3 text-emerald-500" /> Cancel any time</span>
        </div>
      </div>
    </section>
  );
}

// ─── Footer ──────────────────────────────────────────────────────────────

function Footer() {
  return (
    <footer className="bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 grid md:grid-cols-4 gap-8">
        <div>
          <SendVertexLogo
            size={38}
            wordmarkClassName="text-base tracking-tight"
            showTagline
            taglineClassName="text-[9px] tracking-[0.18em]"
            className="gap-2.5"
          />
          <p className="text-xs text-slate-500 mt-3 leading-relaxed max-w-xs">
            The email engine that ships clean lists and pays its bounces forward.
          </p>
        </div>
        {[
          { title: "Product", links: ["Features", "Verification", "Builder", "Deliverability"] },
          { title: "Resources", links: ["FAQ", "Changelog", "Roadmap", "Status"] },
          { title: "Company", links: ["About", "Privacy", "Terms", "Contact"] },
        ].map((col) => (
          <div key={col.title}>
            <div className="text-overline mb-3">{col.title}</div>
            <ul className="space-y-2 text-sm text-slate-600">
              {col.links.map((l) => <li key={l}><a href={`#${l.toLowerCase()}`} className="hover:text-slate-900">{l}</a></li>)}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-slate-200 max-w-7xl mx-auto px-6 lg:px-8 py-6 flex items-center justify-between text-xs text-slate-500 font-mono flex-wrap gap-2">
        <div>© 2026 SendVertex · Built for senders.</div>
        <div>v1.0.0 · ships weekly</div>
      </div>
    </footer>
  );
}
